import { useEffect, useState, useSyncExternalStore } from "react";
import posthog from "posthog-js";

import { isInternalEmail } from "src/helpers/internalUser";
import { newRandomId } from "src/helpers/randomId";

const isPostHogEnabled = () =>
  !window.ENV?.NO_POSTHOG_ACCESS &&
  !!window.ENV?.POSTHOG_PROJECT_TOKEN &&
  !!window.ENV?.POSTHOG_HOST;

/**
 * Identify the current user in PostHog, mirroring the Sentry pattern.
 * Called on app mount and after login — reads from localStorage.
 */
export const setPostHogUser = () => {
  if (!isPostHogEnabled()) return;

  const user = localStorage.getItem("nuclearn_user");
  if (!user || user === "undefined") return;

  try {
    const jsonUser = JSON.parse(user);
    const email: string = jsonUser.username || "";
    // Use email as distinct_id: tenant-local numeric IDs (jsonUser.id) collide
    // across tenants and silently merge separate humans into one PostHog person.
    if (!email) return;
    const roles: string[] = Array.isArray(jsonUser.roles)
      ? jsonUser.roles
          .map((r: { name?: string }) => r?.name)
          .filter((n: unknown): n is string => typeof n === "string")
      : [];

    posthog.identify(email, {
      email,
      name: jsonUser.user_display_name,
      roles,
      license_tier: jsonUser.subscription_fk ?? null,
      account_id: window.ENV?.CUSTOMER_ENV || "",
      signup_date: jsonUser.create_date ?? null,
      is_internal: isInternalEmail(email),
      job_title: jsonUser.profile?.job_title ?? null,
      department: jsonUser.profile?.department ?? null,
      industry: jsonUser.profile?.industry ?? null,
    });
    watchObservationProgramFlag(true);
  } catch (e) {
    console.warn(
      "PostHog identify skipped: malformed nuclearn_user in localStorage",
      e,
    );
  }
};

/**
 * Reset PostHog identity on logout so the next session is anonymous.
 */
export const resetPostHogUser = () => {
  if (!isPostHogEnabled()) return;
  posthog.reset();
  resetObservationProgramFlag();
};

/**
 * Capture a product event, no-op when PostHog is disabled.
 *
 * Use this instead of calling ``posthog.capture`` directly so every
 * call site honors the same enabled-check (NO_POSTHOG_ACCESS,
 * missing token/host). Pass an entry from ``AA_EVENTS`` as ``name``
 * to keep event names a single source of truth.
 */
export const captureEvent = (
  name: string,
  props?: Record<string, unknown>,
): void => {
  if (!isPostHogEnabled()) return;
  posthog.capture(name, props);
};

/**
 * AtomAssist Projects telemetry event names. Centralized so the
 * backend telemetry consumer + the FE both reference the same
 * strings; drift between them silently breaks dashboards.
 *
 * Names mirror the backend ``aa_telemetry.emit`` event ids verbatim
 * (no per-product prefix, spec §5.9) so the two halves of one user
 * action correlate. Events with no backend emit site follow the same
 * unprefixed snake_case convention.
 */
export const AA_EVENTS = {
  GROUP_CREATED: "group_created",
  GROUP_DELETED: "group_deleted",
  GROUP_MEMBER_ADDED: "group_member_added",
  GROUP_MEMBER_REMOVED: "group_member_removed",
  FOLDER_SHARE_REVOKED: "folder_share_revoked",
  FOLDER_ACCESS_DENIED: "folder_access_denied",
  FOLDER_FILE_ADDED: "folder_file_added",
  FOLDER_FILE_ATTACHED_TO_CHAT: "folder_file_attached_to_chat",
  FOLDER_CREATED: "folder_created",
  FOLDER_RESTORED: "folder_restored",
  FOLDER_OWNERSHIP_TRANSFERRED: "folder_ownership_transferred",
  FOLDER_SHARE_GRANTED: "folder_share_granted",
  FOLDER_FILE_ARCHIVED: "folder_file_archived",
  FOLDER_FILE_RESTORED: "folder_file_restored",
  FOLDER_FILE_MOVED: "folder_file_moved",
  FOLDER_ARCHIVED_FILE_DELETED: "folder_archived_file_deleted",
  PROJECT_CHAT_STARTED: "chat_started_in_folder",
  PROJECT_CHAT_SWITCHED: "project_chat_switched",
} as const;

/**
 * Mint a correlation id pairing ai_message_sent with ai_response_completed
 * (#29704). dialogue_id can't serve as the pairing key: it doesn't exist yet
 * when the first send of a new chat is captured, and it can't say which send
 * in a dialogue pairs with which completion.
 */
export const newSendId = (): string => newRandomId();

/**
 * Fire a custom event. Safe to call when PostHog is disabled or hasn't
 * loaded — the SDK silently no-ops, so call sites don't need to guard.
 */
export const trackEvent = (
  event: string,
  properties: Record<string, unknown> = {},
) => {
  if (!isPostHogEnabled()) return;
  try {
    posthog.capture(event, properties);
  } catch (e) {
    console.warn(`PostHog capture failed for ${event}`, e);
  }
};

// ---------- In-app help drawer (FE#1208) ----------
//
// The help drawer uses PostHog feature flags + payloads + surveys. This is a
// separate flag system from Unleash (src/config/featureFlags.ts), which gates
// product modules. PostHog flags here gate the help feature's rollout.

export const HelpFlag = {
  HELP_DRAWER: "in-app-help-drawer",
  CONTACT_SUPPORT: "in-app-contact-support",
  SENTIMENT_SURVEY: "atomassist-sentiment-survey",
} as const;
export type HelpFlagKey = (typeof HelpFlag)[keyof typeof HelpFlag];

// PostHog survey IDs (the `$survey_id` value `posthog.capture("survey sent",...)`
// requires). Created in the PostHog admin UI under Surveys → these UUIDs identify
// the destination survey each submission rolls up into. They are not user-facing.
export const HelpSurvey = {
  CONTACT_SUPPORT: "019e3c2f-0d45-0000-d26c-19e1e0b21c76",
  ATOMASSIST_SENTIMENT: "019e3c35-3eed-0000-9ea5-b516a342fab6",
} as const;

export const isHelpFeatureEnabled = (flag: HelpFlagKey): boolean => {
  // On airgapped on-prem (PostHog disabled), the FAQ drawer still works via
  // bundled JSON in src/assets/help-faq/, so let HELP_DRAWER fall through to
  // enabled. CONTACT_SUPPORT and SENTIMENT_SURVEY actually need the network,
  // so they stay gated.
  if (!isPostHogEnabled()) return flag === HelpFlag.HELP_DRAWER;
  return Boolean(posthog.isFeatureEnabled(flag));
};

export const getHelpFAQPayload = (product: string): unknown => {
  if (!isPostHogEnabled()) return null;
  const payload = posthog.getFeatureFlagPayload(`in-app-help-faq-${product}`);
  return payload ?? null;
};

export const submitHelpSurvey = (
  surveyId: string,
  response: Record<string, unknown>,
): boolean => {
  if (!isPostHogEnabled()) return false;
  posthog.capture("survey sent", {
    $survey_id: surveyId,
    $survey_response: response,
  });
  return true;
};

/**
 * Sidebar nav-item kill switch. Hide an item by creating a boolean
 * PostHog flag named `nav-hide-{navKey}` and enabling it for the
 * target accounts/users. Returns false (hide) only when PostHog
 * returns exactly `true` for that flag; everything else (disabled,
 * undefined, not loaded, PostHog off) shows the item.
 */
export const isNavFeatureEnabled = (navKey: string): boolean => {
  if (!isPostHogEnabled()) return true;
  return posthog.getFeatureFlag(`nav-hide-${navKey}`) !== true;
};

/** Calls `callback` whenever PostHog's flags load and whenever the
 * Observation Program flag's answer changes, its outage fallback included;
 * returns one unsubscribe for both. */
export const onNavFeatureFlagsLoaded = (callback: () => void): (() => void) => {
  if (!isPostHogEnabled()) return () => {};
  const offFlags = posthog.onFeatureFlags(callback);
  const offObservationProgram = subscribeObservationProgramFlag(callback);
  return () => {
    offFlags();
    offObservationProgram();
  };
};

// ── LLM screening rollout gate ────────────────────────────────────────────
//
// Gates every CAP LLM-screening surface (the settings modal and flow node in
// CapAuto, the CAP 2.0 beta view and its v2 toggle) while the feature rolls
// out per-customer. FAIL-CLOSED, deliberately unlike the nav-hide kill
// switch above: this is a rollout gate for a not-yet-GA module, so nothing
// shows unless the flag is enabled for the account. Deployments without
// PostHog (airgapped on-prem) fall back to the deployment config
// (window.ENV.FF_LLM_SCREENING, set in public/config.js), default hidden —
// mirroring the FF_* resolution order documented in featureFlags.ts.
//
// This is the flag leg only. Surfaces also need the cap-llm-screening
// license module, so components gate on useLlmScreeningGate /
// useLlmScreeningEnabled from src/helpers/licensedRolloutGates, which
// combine the two (kept out of this module so it stays free of the API
// client).
export const LLM_SCREENING_FLAG = "cap-llm-screening";

export const isLlmScreeningEnabled = (): boolean => {
  if (!isPostHogEnabled()) return window.ENV?.FF_LLM_SCREENING === true;
  return posthog.isFeatureEnabled(LLM_SCREENING_FLAG) === true;
};

/**
 * Reactive form of isLlmScreeningEnabled: PostHog flags load async after
 * app mount, so a plain call during first render reads `undefined` (hidden)
 * and never updates. Re-evaluates when flags arrive (the sidebar re-render
 * precedent from the nav-hide family).
 */
export const useLlmScreeningFlag = (): {
  enabled: boolean;
  loaded: boolean;
} => {
  const [enabled, setEnabled] = useState(isLlmScreeningEnabled);
  // "Not loaded yet" is distinct from "off": until PostHog answers, a
  // fail-closed reader sees `false`, and anything that ACTS on false (a
  // redirect) would fire on accounts that have the flag. Without PostHog
  // the deployment-config answer is final immediately.
  const [loaded, setLoaded] = useState(!isPostHogEnabled());
  useEffect(() => {
    if (!isPostHogEnabled()) return undefined;
    return posthog.onFeatureFlags(() => {
      setEnabled(isLlmScreeningEnabled());
      setLoaded(true);
    });
  }, []);
  return { enabled, loaded };
};

// ── MCP servers rollout gate ──────────────────────────────────────────────
//
// Gates the "MCP Server (tools)" remote-resource type (FE #2562) while the
// feature rolls out per-customer: the create-modal type option, the per-row
// "Discover & sync tools" action, and the help copy. Same shape as the CAP
// LLM-screening gate above: FAIL-CLOSED, so nothing shows unless the
// `mcp-servers` flag is enabled for the account; deployments without
// PostHog fall back to window.ENV.FF_MCP_SERVERS (public/config.js),
// default hidden. Existing MCP rows still render and edit normally, since
// hiding data an admin already created would be worse than a stray chip.
export const MCP_SERVERS_FLAG = "mcp-servers";

export const isMcpServersEnabled = (): boolean => {
  if (!isPostHogEnabled()) return window.ENV?.FF_MCP_SERVERS === true;
  return posthog.isFeatureEnabled(MCP_SERVERS_FLAG) === true;
};

/** Render-only gating: PostHog flags load async after mount, so this
 * re-evaluates when they arrive. A late false→true flip just reveals the
 * option; nothing navigates or persists on the answer. */
export const useMcpServersEnabled = (): boolean => {
  const [enabled, setEnabled] = useState(isMcpServersEnabled);
  useEffect(() => {
    if (!isPostHogEnabled()) return undefined;
    return posthog.onFeatureFlags(() => setEnabled(isMcpServersEnabled()));
  }, []);
  return enabled;
};

// ── Edge connectors rollout gate ─────────────────────────────────────────────
//
// Gates the Edge Connectors admin page (both edge-agent planes)
// while it rolls out per-customer. Same shape as the gates above:
// FAIL-CLOSED on the `edge-connectors` flag; deployments without PostHog fall
// back to window.ENV.FF_EDGE_CONNECTORS (public/config.js), default hidden. The
// page also needs the `edge-connectors` license module and the admin role,
// which the route's _product/_roles enforce.
export const EDGE_CONNECTORS_FLAG = "edge-connectors";

export const isEdgeConnectorsEnabled = (): boolean => {
  if (!isPostHogEnabled()) return window.ENV?.FF_EDGE_CONNECTORS === true;
  return posthog.isFeatureEnabled(EDGE_CONNECTORS_FLAG) === true;
};

/** Reactive form with a `loaded` bit, so a route redirect waits for
 * PostHog to answer instead of bouncing flagged accounts on a cold load. */
export const useEdgeConnectorsGate = (): {
  enabled: boolean;
  loaded: boolean;
} => {
  const [enabled, setEnabled] = useState(isEdgeConnectorsEnabled);
  const [loaded, setLoaded] = useState(!isPostHogEnabled());
  useEffect(() => {
    if (!isPostHogEnabled()) return undefined;
    return posthog.onFeatureFlags(() => {
      setEnabled(isEdgeConnectorsEnabled());
      setLoaded(true);
    });
  }, []);
  return { enabled, loaded };
};

// ── Smart CR rollout gate ─────────────────────────────────────────────────
//
// Gates every Smart CR surface (the CR workspace, Draft Box and intake, the
// System of Record browser and detail, CR configuration and lifecycle admin,
// their sidebar and CAP AI entry points, and the agent's draft hand-off
// card). Same shape as the CAP LLM-screening gate above: FAIL-CLOSED, so
// nothing shows unless the `smart-cr` flag is enabled for the account;
// deployments without PostHog fall back to window.ENV.FF_SMART_CR
// (public/config.js), default hidden. The backend enforces the same flag
// (and the same FF_SMART_CR name) on every Smart CR route and the agent tool.
//
// This is the flag leg only. Surfaces also need the smart-cr license module,
// so components gate on useSmartCrGate / useSmartCrEnabled from
// src/helpers/licensedRolloutGates, and the sidebar item pairs
// `_enabled: isSmartCrEnabled` with `_product: "smart-cr"`.
export const SMART_CR_FLAG = "smart-cr";

export const isSmartCrEnabled = (): boolean => {
  if (!isPostHogEnabled()) return window.ENV?.FF_SMART_CR === true;
  return posthog.isFeatureEnabled(SMART_CR_FLAG) === true;
};

/**
 * Reactive form of isSmartCrEnabled. PostHog flags load async after mount,
 * so `loaded` distinguishes "not answered yet" from "off": anything that
 * acts on false (a redirect, a not-enabled notice) must wait for it. Without
 * PostHog the deployment-config answer is final immediately.
 */
export const useSmartCrFlag = (): { enabled: boolean; loaded: boolean } => {
  const [enabled, setEnabled] = useState(isSmartCrEnabled);
  const [loaded, setLoaded] = useState(!isPostHogEnabled());
  useEffect(() => {
    if (!isPostHogEnabled()) return undefined;
    return posthog.onFeatureFlags(() => {
      setEnabled(isSmartCrEnabled());
      setLoaded(true);
    });
  }, []);
  return { enabled, loaded };
};

// Gates the observation interview (the Observation Program page and OP Chat)
// and its sidebar entries; the OP Dashboard, its insights and analysis chats
// are not gated. Same shape as the Smart CR gate above: FAIL-CLOSED, so the
// interview shows only where the `observation-program` flag is enabled for the
// account; deployments without PostHog fall back to
// window.ENV.FF_OBSERVATION_PROGRAM (public/config.js), default hidden. The
// backend reads the same FF_OBSERVATION_PROGRAM name.
//
// The interview also needs the observation-program license module: its pages
// gate on useObservationInterviewGate from src/helpers/licensedRolloutGates,
// and its sidebar items pair `_enabled: isObservationProgramEnabled` with
// `_product: ["observation-program"]`.
export const OBSERVATION_PROGRAM_FLAG = "observation-program";

const observationProgramFallback = (): boolean =>
  window.ENV?.FF_OBSERVATION_PROGRAM === true;

/** Whether the Observation Program surfaces show: FF_OBSERVATION_PROGRAM
 * without PostHog, else the same answer as useObservationProgramFlag, false
 * until it has loaded. */
export const isObservationProgramEnabled = (): boolean => {
  if (!isPostHogEnabled()) return observationProgramFallback();
  return readObservationProgramFlag().enabled;
};

// PostHog abandons /flags after 3 s; past this the deployment's own setting decides.
const OBSERVATION_PROGRAM_FLAG_WAIT_MS = 5000;

type ObservationProgramFlag = { enabled: boolean; loaded: boolean };

let observationProgramFlag: ObservationProgramFlag = {
  enabled: false,
  loaded: false,
};
let observationProgramWatched = false;
// A sign-out holds the watch until the next user is identified, so their flag is never the anonymous visitor's.
let observationProgramHeld = false;
let stopObservationProgramWatch = () => {};
const observationProgramListeners = new Set<() => void>();

const setObservationProgramFlag = (next: ObservationProgramFlag) => {
  if (
    next.enabled === observationProgramFlag.enabled &&
    next.loaded === observationProgramFlag.loaded
  ) {
    return;
  }
  observationProgramFlag = next;
  observationProgramListeners.forEach((listener) => listener());
};

/** Starts the one watch that resolves the flag from PostHog's answers, at
 * the latest when a user is identified (`identified`, which also lifts a
 * sign-out's hold); later calls, and calls without PostHog, do nothing. */
const watchObservationProgramFlag = (identified = false) => {
  if (identified) observationProgramHeld = false;
  // Before init PostHog answers once with an error and keeps no listener, so nothing is watched until it has loaded.
  if (
    observationProgramWatched ||
    observationProgramHeld ||
    !isPostHogEnabled() ||
    !posthog.__loaded
  )
    return;
  observationProgramWatched = true;
  const timer = window.setTimeout(
    () =>
      setObservationProgramFlag({
        enabled: observationProgramFallback(),
        loaded: true,
      }),
    OBSERVATION_PROGRAM_FLAG_WAIT_MS,
  );
  const settle = (enabled: boolean) => {
    window.clearTimeout(timer);
    setObservationProgramFlag({ enabled, loaded: true });
  };
  let replaying = true;
  const off = posthog.onFeatureFlags((_flags, _variants, context) => {
    const answer = posthog.isFeatureEnabled(OBSERVATION_PROGRAM_FLAG);
    if (context?.errorsLoading) {
      // Unanswered, PostHog's last answer stands, else the deployment's setting, as on the backend.
      settle(answer ?? observationProgramFallback());
    } else if (replaying && answer !== true) {
      // The flags already held can predate identify, so a no from them is asked again before the gate acts on it.
      posthog.reloadFeatureFlags();
    } else {
      settle(answer === true);
    }
  });
  replaying = false;
  stopObservationProgramWatch = () => {
    window.clearTimeout(timer);
    off();
  };
};

/** Drops the watch and its answer, so the next user's flag is read afresh. */
const resetObservationProgramFlag = () => {
  stopObservationProgramWatch();
  stopObservationProgramWatch = () => {};
  observationProgramWatched = false;
  observationProgramHeld = true;
  setObservationProgramFlag({ enabled: false, loaded: false });
};

const subscribeObservationProgramFlag = (listener: () => void) => {
  observationProgramListeners.add(listener);
  watchObservationProgramFlag();
  return () => {
    observationProgramListeners.delete(listener);
  };
};

const readObservationProgramFlag = (): ObservationProgramFlag => {
  watchObservationProgramFlag();
  return observationProgramFlag;
};

/** Reactive form of isObservationProgramEnabled, sharing one answer with the
 * sidebar; `loaded` as for useSmartCrFlag. A no from flags PostHog already
 * held when the flag is first read is asked again before it counts, and a
 * PostHog that errors or never answers falls back to FF_OBSERVATION_PROGRAM. */
export const useObservationProgramFlag = (): {
  enabled: boolean;
  loaded: boolean;
} => {
  const flag = useSyncExternalStore(
    subscribeObservationProgramFlag,
    readObservationProgramFlag,
  );
  return isPostHogEnabled()
    ? flag
    : { enabled: observationProgramFallback(), loaded: true };
};

export const captureHelpEvent = (
  event: "help_panel_opened" | "help_faq_item_expanded",
  props: Record<string, unknown>,
): void => {
  if (!isPostHogEnabled()) return;
  posthog.capture(event, props);
};
